import { SparkForPubMetricsByTime } from '@pubstack/common/src/analytics/query'
import { CurrencySymbol } from '@pubstack/common/src/currency'
import { Timezone } from '@pubstack/common/src/timezone'
import { DateTime } from 'luxon'
import { FunctionComponent, useMemo, useState } from 'react'
import { Colors } from '~/assets/style/colors'
import { Tabs, TabProp } from '~/components/Tabs'
import { WidgetProps } from '~/components/Widget'
import { AnalyticsDefaultChartOptions } from '~/modules/analytics/AnalyticsCharts'
import { AnalyticsChartWidget } from '~/modules/analytics/AnalyticsChartWidget'
import { MetricConfiguration, TimelineConfiguration, useMetrics, useTimelineChart } from '~/modules/analytics/analyticsTimeline.hooks'
import { DATA_TYPES } from '~/modules/analytics/common/shared'
import {
  serverSideImpressionCount,
  sparkForPubAdCallCount,
  sparkForPubBaselineRevenue,
  sparkForPubBaselineRpm,
  sparkForPubRpmUplift,
  sparkForPubTotalRevenue,
  sparkForPubTotalRpm,
  sparkForPubUpliftRevenue,
  sparkForPubUpliftRpm,
} from '~/modules/analytics/common/formulas'
import { WithClassName } from '~/types/utils'
import { ANALYTICS_TOOLTIPS } from '~/utils/constants'
import { displayWithCurrency } from '~/utils/string'

type DataType = (typeof DATA_TYPES)[number]['type']

// Metrics whose chart is a baseline/uplift stack — the only ones where the % (100%-stacked) view is meaningful.
const PERCENTAGE_METRICS = ['revenue', 'rpm']

const useConfig = (type: DataType) => {
  return useMemo((): (MetricConfiguration<SparkForPubMetricsByTime> & TimelineConfiguration<SparkForPubMetricsByTime>)[] => {
    const baseline = { name: 'baseline', label: 'Baseline', iconColor: Colors.SteelBlue }
    const uplift = { name: 'uplift', label: 'Spark Uplift', iconColor: Colors.Frog }
    const legendConfig = ['baseline', 'uplift']
    return [
      {
        getChartOptions: ({ currencySymbol }) =>
          type === 'percentage'
            ? { seriesType: 'bars', isStacked: 'relative', vAxis: { ...AnalyticsDefaultChartOptions.vAxis, format: 'percent', minValue: 0 } }
            : { seriesType: 'bars', isStacked: true, vAxis: { ...AnalyticsDefaultChartOptions.vAxis, format: displayWithCurrency('#.##', currencySymbol) } },
        metric: {
          name: 'revenue',
          tooltipText: ANALYTICS_TOOLTIPS.REVENUE,
          message: ({ data, currencySymbol }) => {
            if (!sparkForPubUpliftRevenue.isComputable(data) || !sparkForPubRpmUplift.isComputable(data)) {
              return undefined
            }
            // sparkUpliftCpmSum is precomputed server-side per (bidder, time bucket) and floored
            // there — see query-spark-for-pub.ts — so this sum is linear and equals the sum of the
            // stacked bars exactly, unlike before. pct stays a ratio (uplift / baseline summed over
            // the period), which is correct for a percentage and still isn't the sum of per-bucket %s.
            const upliftSum = sparkForPubUpliftRevenue.sum(data)
            const pct = sparkForPubRpmUplift.sum(data)
            if (upliftSum <= 0 || Number.isNaN(pct)) {
              return undefined // no control sample / uplift nul (or floored to 0) → banner hidden
            }
            return {
              icon: 'rocket',
              message: (
                <>
                  Spark for Publishers gave you <b>+{sparkForPubUpliftRevenue.displayable(upliftSum, currencySymbol)} in revenue</b> (<b>+{pct.toFixed(0)}%</b>).
                </>
              ),
            }
          },
        },
        legendConfig,
        tooltipConfig: [legendConfig, ['revenue']],
        dataConfig: [
          {
            name: 'revenue',
            label: sparkForPubTotalRevenue.name,
            isComputable: ({ data }) => sparkForPubTotalRevenue.isComputable(data),
            getFormula: () => sparkForPubTotalRevenue,
          },
          { ...baseline, isComputable: ({ data }) => sparkForPubBaselineRevenue.isComputable(data), getFormula: () => sparkForPubBaselineRevenue },
          { ...uplift, isComputable: ({ data }) => sparkForPubUpliftRevenue.isComputable(data), getFormula: () => sparkForPubUpliftRevenue },
        ],
      },
      {
        getChartOptions: ({ currencySymbol }) =>
          type === 'percentage'
            ? { seriesType: 'bars', isStacked: 'relative', vAxis: { ...AnalyticsDefaultChartOptions.vAxis, format: 'percent', minValue: 0 } }
            : { seriesType: 'bars', isStacked: true, vAxis: { ...AnalyticsDefaultChartOptions.vAxis, format: displayWithCurrency('#.##', currencySymbol) } },
        metric: { name: 'rpm', tooltipText: ANALYTICS_TOOLTIPS.SPARK_FOR_PUB_AD_CALL_RPM },
        legendConfig: ['baselineRpm', 'upliftRpm'],
        tooltipConfig: [['baselineRpm', 'upliftRpm'], ['rpm']],
        dataConfig: [
          { name: 'rpm', label: sparkForPubTotalRpm.name, isComputable: ({ data }) => sparkForPubTotalRpm.isComputable(data), getFormula: () => sparkForPubTotalRpm },
          { name: 'baselineRpm', label: baseline.label, iconColor: Colors.SteelBlue, isComputable: ({ data }) => sparkForPubBaselineRpm.isComputable(data), getFormula: () => sparkForPubBaselineRpm },
          { name: 'upliftRpm', label: 'Spark Uplift', iconColor: Colors.Frog, isComputable: ({ data }) => sparkForPubUpliftRpm.isComputable(data), getFormula: () => sparkForPubUpliftRpm },
        ],
      },
      {
        getChartOptions: () => ({ seriesType: 'bars' }),
        metric: { name: 'auction', tooltipText: ANALYTICS_TOOLTIPS.AUCTION_COUNT },
        legendConfig: ['auction'],
        tooltipConfig: ['auction'],
        dataConfig: [{ name: 'auction', label: sparkForPubAdCallCount.name, getFormula: ({ data }) => (sparkForPubAdCallCount.isComputable(data) ? sparkForPubAdCallCount : undefined) }],
      },
      {
        getChartOptions: () => ({ seriesType: 'bars' }),
        metric: { name: 'impressionCount' },
        legendConfig: ['impressionCount'],
        tooltipConfig: ['impressionCount'],
        dataConfig: [
          { name: 'impressionCount', label: serverSideImpressionCount.name, getFormula: ({ data }) => (serverSideImpressionCount.isComputable(data) ? serverSideImpressionCount : undefined) },
        ],
      },
    ]
  }, [type])
}

type PureSparkForPubTimelineProps = WithClassName &
  Omit<WidgetProps, 'title' | 'info' | 'icon' | 'refreshTrackingLabel'> & {
    data: SparkForPubMetricsByTime
    currencySymbol: CurrencySymbol
    currentEpoch: DateTime
    timezone: Timezone
    onMetricChange: (metric: string) => void
  }
export const PureSparkForPubTimeline: FunctionComponent<PureSparkForPubTimelineProps> = ({ data, currencySymbol, currentEpoch, timezone, onMetricChange, ...props }) => {
  const [typeTab, setTypeTab] = useState(0)
  const type = DATA_TYPES[typeTab].type
  const config = useConfig(type)
  const { metrics, currentConfig, currentMetric, metricMessage } = useMetrics({ config, data, currencySymbol, onMetricChange })
  const { chart, legends } = useTimelineChart({ currentConfig, data: currentMetric.notApplicable ? { epoch: [] as string[] } : data, currencySymbol, currentEpoch, timezone, type })

  // % (100%-stacked) view only makes sense for the baseline/uplift stacks — disable it on single-series metrics.
  const tabs: TabProp[] = DATA_TYPES.map((dataType, index) => ({
    label: dataType.label,
    active: typeTab === index,
    disabled: dataType.type === 'percentage' && !PERCENTAGE_METRICS.includes(currentConfig.metric.name),
    trackingLabel: `ana-spark-for-pub-timeline-${dataType.type}-tab-apply`,
  }))
  const onTabClick = (tab: TabProp) => setTypeTab(tabs.indexOf(tab))

  return (
    <AnalyticsChartWidget
      {...props}
      icon={'chart_bar'}
      title={'Performance'}
      info={<Tabs tabs={tabs} fluid={false} onClick={onTabClick} />}
      metrics={metrics}
      chart={chart}
      legends={legends}
      metricMessage={metricMessage}
      refreshTrackingLabel={'ana-spark-for-pub-timeline-refresh'}
    />
  )
}
